import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/api";
import { useAuth } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Spinner, Overline } from "@/components/Brand";
import { toast } from "sonner";
import { Store, ScanLine, Clock, Heart, BookMarked, Search, MapPin, Star } from "lucide-react";

export const visitorNav = [
  { key: "explore", to: "/visitor", label: "Explore", icon: Store },
  { key: "scan", to: "/visitor/scan", label: "Scan", icon: ScanLine },
  { key: "history", to: "/visitor/history", label: "History", icon: Clock },
  { key: "favourites", to: "/visitor/favourites", label: "Saved", icon: Heart },
  { key: "summary", to: "/visitor/summary", label: "Summary", icon: BookMarked },
];

export default function VisitorDirectory() {
  const [exhibitors, setExhibitors] = useState(null);
  const [cats, setCats] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = async () => {
    const [{ data: ex }, { data: favs }] = await Promise.all([
      api.get("/exhibitors", { params: { search: search || undefined, category: cat !== "All" ? cat : undefined } }),
      api.get("/visitor/favourite-ids"),
    ]);
    setExhibitors(ex);
    setFavIds(favs);
  };

  useEffect(() => {
    api.get("/exhibitors/categories").then(({ data }) => setCats(["All", ...data]));
  }, []);
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [search, cat]);

  const toggleFav = async (e, id) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/visitor/favourites/${id}`);
      setFavIds((p) => (data.favourited ? [...p, id] : p.filter((x) => x !== id)));
      toast.success(data.favourited ? "Added to shortlist" : "Removed");
    } catch (err) {
      toast.error(apiErr(err));
    }
  };

  return (
    <Shell nav={visitorNav}>
      <Overline>Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</Overline>
      <h1 className="font-display text-3xl sm:text-4xl gold-text mt-2">Discover Exhibitors</h1>
      <p className="text-slate-400 text-sm mt-1">Browse stalls, plan your visit, save favourites.</p>

      <div className="mt-6 relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          data-testid="exhibitor-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search exhibitors, cities…"
          className="w-full bg-black/30 gold-border rounded-xl pl-11 pr-4 py-3 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto mt-4 pb-1" style={{ scrollbarWidth: "none" }}>
        {cats.map((c) => (
          <button
            key={c}
            data-testid={`category-${c}`}
            onClick={() => setCat(c)}
            className={`whitespace-nowrap text-xs px-4 py-2 rounded-full border transition ${cat === c ? "btn-gold border-transparent" : "glass text-slate-300 border-amber-500/20"}`}
          >
            {c}
          </button>
        ))}
      </div>

      {!exhibitors ? (
        <Spinner />
      ) : exhibitors.length === 0 ? (
        <p className="text-slate-400 text-center py-16">No exhibitors match your search.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
          {exhibitors.map((ex, i) => (
            <div
              key={ex.id}
              data-testid={`exhibitor-card-${ex.id}`}
              onClick={() => navigate(`/visitor/exhibitor/${ex.id}`)}
              style={{ animationDelay: `${i * 50}ms` }}
              className="glass rounded-2xl overflow-hidden card-lift fade-up cursor-pointer"
            >
              <div className="h-36 bg-cover bg-center relative" style={{ backgroundImage: `url(${ex.logo_url})` }}>
                <div className="absolute inset-0 bg-gradient-to-t from-[#07130F] via-transparent to-transparent" />
                <button
                  data-testid={`favourite-exhibitor-button-${ex.id}`}
                  onClick={(e) => toggleFav(e, ex.id)}
                  className="absolute top-3 right-3 h-9 w-9 rounded-full glass flex items-center justify-center"
                >
                  <Heart size={16} className={favIds.includes(ex.id) ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                </button>
              </div>
              <div className="p-4">
                <h3 className="font-serif-head text-xl text-slate-100">{ex.name}</h3>
                <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                  <MapPin size={12} /> {ex.hall} · {ex.booth} · {ex.city}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {ex.categories.slice(0, 3).map((c) => (
                    <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20">{c}</span>
                  ))}
                </div>
                <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-3">
                  <Star size={11} className="text-amber-400" /> {ex.catalogue_count} catalogue{ex.catalogue_count !== 1 ? "s" : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}
